import { Dumbbell, MoreVertical, Pencil, Plus, Timer, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { archiveExercise, createExercise, getEquipments, getExercises, updateExercise } from '@/api/services/workout'
import type { Equipment, Exercise, ExerciseCreateInput } from '@/api/types/workout'
import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { confirmDialog } from '@/lib/confirm-dialog'

import { MuscleBadges, MusclePicker } from './muscle-body/muscle-picker'

type Props = { onClose: () => void }

const CATEGORY_LABELS: Record<string, string> = {
  chest: '胸',
  back: '背中',
  legs: '脚',
  shoulders: '肩',
  arms: '腕',
  abs: '腹',
  compound: '複合',
}

const EMPTY_FORM: ExerciseCreateInput = {
  name: '',
  category: '',
  equipments: [],
  muscles: [],
  default_rest_seconds: 90,
  notes: '',
}

export const ExercisesManager = ({ onClose }: Props) => {
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [allEquipments, setAllEquipments] = useState<Equipment[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Exercise | null>(null)
  const [form, setForm] = useState<ExerciseCreateInput>(EMPTY_FORM)
  const [open, setOpen] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [exs, eqs] = await Promise.all([getExercises(false), getEquipments()])
      setExercises(exs)
      setAllEquipments(eqs)
    } catch (err) {
      console.error(err)
      toast.error('種目の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const openNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setOpen(true)
  }

  const openEdit = (e: Exercise) => {
    setEditing(e)
    setForm({
      name: e.name,
      category: e.category ?? '',
      equipments: [...e.equipments],
      muscles: [...e.muscles],
      default_rest_seconds: e.default_rest_seconds,
      notes: e.notes ?? '',
    })
    setOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('種目名を入力してください')
      return
    }
    try {
      if (editing) {
        await updateExercise(editing.id, form)
      } else {
        await createExercise(form)
      }
      setOpen(false)
      await reload()
    } catch (err) {
      console.error(err)
      toast.error('保存に失敗しました')
    }
  }

  const handleArchive = async (e: Exercise) => {
    if (!(await confirmDialog({ title: `「${e.name}」をアーカイブしますか？`, confirmLabel: 'アーカイブ' }))) return
    try {
      await archiveExercise(e.id)
      await reload()
    } catch (err) {
      console.error(err)
      toast.error('アーカイブに失敗しました')
    }
  }

  return (
    <div className="flex flex-col h-screen">
      <Header title="種目マスタ" onClose={onClose} />
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        <Button onClick={openNew} className="w-full mb-2">
          <Plus className="size-4 mr-1" /> 新規種目を追加
        </Button>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">読み込み中...</div>
        ) : exercises.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">種目がありません</div>
        ) : (
          <div className="divide-y divide-border rounded-lg border overflow-hidden">
            {exercises.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{e.name}</span>
                    {e.category && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {CATEGORY_LABELS[e.category] ?? e.category}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                    {e.equipments.length > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Dumbbell className="size-3" />
                        {e.equipments.join('・')}
                      </span>
                    )}
                    <span className="flex items-center gap-0.5">
                      <Timer className="size-3" />
                      {e.default_rest_seconds}s
                    </span>
                  </div>
                  <MuscleBadges muscles={e.muscles} />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8 shrink-0">
                      <MoreVertical className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(e)}>
                      <Pencil className="size-3.5 mr-2" /> 編集
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => handleArchive(e)}>
                      <Trash2 className="size-3.5 mr-2" /> アーカイブ
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? '種目を編集' : '新規種目'}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto">
            <div>
              <Label>種目名</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>筋群</Label>
              <MusclePicker value={form.muscles ?? []} onChange={(muscles) => setForm({ ...form, muscles })} />
            </div>
            <div>
              <Label>カテゴリ（任意）</Label>
              <Input
                value={form.category ?? ''}
                placeholder="例: chest, back, legs"
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </div>
            <div>
              <Label>器具（複数選択可）</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {allEquipments.map((eq) => {
                  const checked = form.equipments?.includes(eq.name) ?? false
                  return (
                    <label key={eq.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => {
                          const current = form.equipments ?? []
                          setForm({
                            ...form,
                            equipments: c ? [...current, eq.name] : current.filter((n) => n !== eq.name),
                          })
                        }}
                      />
                      {eq.name}
                    </label>
                  )
                })}
              </div>
            </div>
            <div>
              <Label>既定の休憩秒数</Label>
              <Input
                type="number"
                value={form.default_rest_seconds ?? 90}
                onChange={(e) => setForm({ ...form, default_rest_seconds: Number(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>メモ</Label>
              <Textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSubmit}>{editing ? '保存' : '追加'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
